var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import globalLogger from "@lazarentertainment/js-logger";
import createPubSub from "@lazarentertainment/js-pubsub";
import { ChatPlugin } from "./chatPlugin";
import { DEFAULT_STATE_CHANGE_THROTTLE_MS, } from "./chatState";
import { DisplayNameHandler } from "./displayNameHandler";
import { createChatStorage } from "./localChatData";
import { DEFAULT_CHAT_MODE, PubNubChatClient, } from "./pubNubChatClient";
import { STAGE_TEN_SOURCE_NAME } from "./types";
import { isChatError, displayNameRejectedError } from "./error";
import { NotificationState, } from "./notificationState";
function throwNeedsInitError() {
    throw new Error("You must call init() first");
}
const defaultPubNubNotificationClientState = {
    notifications: new Map(),
};
export function createChatFacade({ apolloClient, pubnubPublishKey, pubnubSubscribeKey, environment, s10ChannelId, defaultDisplayName, storage = createChatStorage(), onStateChangeThrottleMs = DEFAULT_STATE_CHANGE_THROTTLE_MS, mode = DEFAULT_CHAT_MODE, }) {
    const pubSub = createPubSub();
    let chatClient;
    let closed = false;
    const onGrantResultUpdate = ({ userId, pubnubAccessToken, }) => chatClient === null || chatClient === void 0 ? void 0 : chatClient.updateAuth(userId, pubnubAccessToken);
    const chatPlugin = new ChatPlugin({
        apolloClient,
        s10ChannelId,
        pubSub,
        onGrantResultUpdate,
    });
    const displayNameHandler = new DisplayNameHandler(chatPlugin, pubSub, storage, defaultDisplayName);
    const notificationState = new NotificationState((state) => {
        if (closed) {
            return;
        }
        pubSub.publish("notificationStateChange", state);
    });
    const onTokenExpired = () => __awaiter(this, void 0, void 0, function* () {
        if (closed) {
            return;
        }
        globalLogger.log("PubNub token expired. Fetching a new grant result...");
        try {
            onGrantResultUpdate(yield chatPlugin.loadGrantResult());
        }
        catch (error) {
            if (closed) {
                return;
            }
            // TODO: Some error handling for this
            globalLogger.error(error);
        }
    });
    const init = () => __awaiter(this, void 0, void 0, function* () {
        if (closed) {
            return;
        }
        const [, { pubnubAccessToken, userId, roomKey }] = yield Promise.all([
            chatPlugin.loadPluginState(),
            chatPlugin.loadGrantResult(),
        ]);
        if (chatClient || closed) {
            return;
        }
        chatClient = new PubNubChatClient({
            s10ChannelId,
            userId,
            roomKey,
            pubnubAccessToken,
            publishKey: pubnubPublishKey,
            subscribeKey: pubnubSubscribeKey,
            onStateChangeThrottleMs,
            mode,
            pubSub,
            notificationState,
            onTokenExpired,
        });
        pubSub.publish("initialized", true);
    });
    const publishAndThrow = (error) => {
        if (isChatError(error)) {
            pubSub.publish("error", error);
        }
        throw error;
    };
    const getDisplayNameOrThrow = () => {
        const displayName = displayNameHandler.getDisplayName();
        if (!displayName) {
            throw new Error("display name not set");
        }
        return displayName;
    };
    const submitMessage = (message) => {
        var _a;
        if (closed) {
            return;
        }
        if (!chatClient) {
            throwNeedsInitError();
        }
        if (chatPlugin.getPluginState() !== "enabled") {
            throw new Error("Chat plugin is not enabled");
        }
        const displayName = getDisplayNameOrThrow();
        if (displayNameHandler.isDisplayNameInvalid(displayName)) {
            throw displayNameRejectedError(displayName);
        }
        const trimmedMessage = message.trim();
        if (!trimmedMessage) {
            throw new Error("Message was empty");
        }
        try {
            const { messageKey, promise } = chatClient.enqueueMessage({
                message: trimmedMessage,
                environment,
                sourceName: STAGE_TEN_SOURCE_NAME,
            }, ((_a = chatPlugin.grantResult) === null || _a === void 0 ? void 0 : _a.isBanned) === true, getDisplayNameOrThrow);
            return {
                messageKey,
                promise: promise.then(({ type, value }) => {
                    if (type === "aborted") {
                        return null;
                    }
                    return value;
                }),
            };
        }
        catch (error) {
            publishAndThrow(error);
        }
    };
    const toggleReaction = (reactionKey, messageTimetoken) => {
        if (closed) {
            return;
        }
        if (!chatClient) {
            throwNeedsInitError();
        }
        const reactionTimetoken = chatClient.getReactionTimetoken(reactionKey, messageTimetoken);
        if (reactionTimetoken) {
            return chatClient.removeReaction(reactionKey, messageTimetoken, reactionTimetoken);
        }
        return chatClient.addReaction(reactionKey, messageTimetoken);
    };
    const loadMessages = (args) => {
        if (closed) {
            return Promise.resolve();
        }
        if (!chatClient) {
            throwNeedsInitError();
        }
        return chatClient.loadMessages(args);
    };
    const fetchMessages = (args) => {
        if (closed) {
            return Promise.resolve([]);
        }
        if (!chatClient) {
            throwNeedsInitError();
        }
        return chatClient.fetchMessages(args);
    };
    const deleteMessage = ({ timetoken, messageKey, }) => __awaiter(this, void 0, void 0, function* () {
        if (closed) {
            return;
        }
        if (!chatClient) {
            throwNeedsInitError();
        }
        try {
            yield chatClient.deleteMessage(timetoken !== null && timetoken !== void 0 ? timetoken : messageKey);
        }
        catch (error) {
            if (closed) {
                return;
            }
            if (isChatError(error)) {
                pubSub.publish("error", error);
            }
            throw error;
        }
    });
    const close = () => {
        if (closed) {
            return;
        }
        closed = true;
        chatClient === null || chatClient === void 0 ? void 0 : chatClient.close();
        chatClient = undefined;
        chatPlugin.close();
        displayNameHandler.close();
        pubSub.publish("initialized", false);
    };
    const defaultPubNubClientState = {
        messages: new Map(),
        reactions: {},
    };
    const getPubNubClientState = () => {
        if (!chatClient) {
            return defaultPubNubClientState;
        }
        return chatClient.state;
    };
    const getPubNubNotificationClientState = () => {
        if (!chatClient) {
            return defaultPubNubNotificationClientState;
        }
        return notificationState.state;
    };
    const clearState = () => {
        notificationState.clear();
        chatClient === null || chatClient === void 0 ? void 0 : chatClient.clearState();
    };
    const onStateChange = (callback) => {
        pubSub.subscribe("stateChange", callback);
    };
    const offStateChange = (callback) => {
        pubSub.unsubscribe("stateChange", callback);
    };
    const onNotificationStateChange = (callback) => {
        pubSub.subscribe("notificationStateChange", callback);
    };
    const offNotificationStateChange = (callback) => {
        pubSub.unsubscribe("notificationStateChange", callback);
    };
    const onChatEvent = (event, callback) => {
        pubSub.subscribe(event, callback);
        return () => pubSub.unsubscribe(event, callback);
    };
    const getLoadedRangeIntersecting = (timetoken) => chatClient === null || chatClient === void 0 ? void 0 : chatClient.getLoadedRangeIntersecting(timetoken);
    const getQuotaRefreshSecondsRemaining = () => { var _a; return (_a = chatClient === null || chatClient === void 0 ? void 0 : chatClient.quotaRefreshSecondsRemaining) !== null && _a !== void 0 ? _a : 0; };
    const getIsChannelOwner = () => { var _a; return (_a = chatClient === null || chatClient === void 0 ? void 0 : chatClient.isChannelOwner) !== null && _a !== void 0 ? _a : false; };
    return {
        getPluginState: chatPlugin.getPluginState,
        getInitializedState: () => !!chatClient,
        getDisplayName: displayNameHandler.getDisplayName,
        getBans: chatPlugin.getBans,
        getApiKey: chatPlugin.getApiKey,
        getQuotaRefreshSecondsRemaining,
        onStateChange,
        offStateChange,
        onNotificationStateChange,
        offNotificationStateChange,
        onChatEvent,
        getPubNubClientState,
        getPubNubNotificationClientState,
        clearState,
        subscribe: pubSub.subscribe,
        unsubscribe: pubSub.unsubscribe,
        init,
        updateDisplayName: displayNameHandler.updateDisplayName,
        submitMessage,
        loadMessages,
        fetchMessages,
        toggleReaction,
        deleteMessage,
        banUser: chatPlugin.banUser,
        unbanUser: chatPlugin.unbanUser,
        regenerateApiKey: chatPlugin.regenerateApiKey,
        loadApiKey: chatPlugin.loadApiKey,
        unbanAllUsers: chatPlugin.unbanAllUsers,
        loadBans: chatPlugin.loadBans,
        toggleChatEnabledState: chatPlugin.toggleChatEnabledState,
        close,
        getLoadedRangeIntersecting,
        getIsChannelOwner,
    };
}
